import { Attachment } from '@digital-platform/attachments/domain';
import { DomainModelHelper } from '@digital-platform/shared/domain';
import {
  AdditionalSupplement,
  ExpressWasteProfile,
  ExpressWasteProfileMeta,
  TruxDescription,
  WasteProfile,
} from '../interfaces';
import { DynamoSafeExpressWasteProfile } from '../interfaces/express-waste-profile.interface';
import { AdditionalSupplementModel } from './additional-supplement.model';
import { ExpressWasteProfileMetaModel } from './express-waste-profile-meta.model';
import { TruxDescriptionModel } from './trux-description.model';
import { WasteProfileModel } from './waste-profile.model';

export class ExpressWasteProfileModel
  extends WasteProfileModel
  implements ExpressWasteProfile
{
  static readonly DEFAULT_CONFIG: ExpressWasteProfile = {
    ...WasteProfileModel.DEFAULT_CONFIG,
    type: 'express',
    meta: {
      sections: {
        generator: {
          completed: false,
          valid: false,
        },
        billing: {
          completed: false,
          valid: false,
        },
        authorization: {
          completed: false,
          valid: false,
        },
        wasteStream: {
          completed: false,
          valid: false,
        },
        attachments: {
          completed: false,
          valid: false,
        },
        destination: {
          completed: false,
          valid: false,
        },
        checkList: {
          completed: false,
          valid: false,
        },
        signature: {
          completed: false,
          valid: false,
        },
      },
    },
    wasteTruxCode: null,
    sampleTaken: null,
    methodOfShipment: null,
    otherMethodOfShipment: '',
    frequency: null,
    estimatedVolume: null,
    unitOfMeasure: null,
    dominantColor: null,
    otherColor: [],
    odorStrength: null,
    odorDescription: null,
    stateApprovalWasteCode: null,
    stateApprovalWasteCodeNotApplicable: null,
    additionalSupplements: [],
    stateAttachments: [],
    internalOnlyAttachments: [],
    additionalAdminSupplements: [],
    internalAdminAttachments: [],
    thirdPartyAuthAdminAttachment: null,
    stateInternalAdminAttachments: [],
  };

  // other data
  readonly type: 'express';
  readonly meta: ExpressWasteProfileMeta;

  // waste stream step
  readonly wasteTruxCode: TruxDescriptionModel;
  readonly sampleTaken: boolean;
  readonly methodOfShipment: 'bulk' | 'drum' | 'bagged' | 'other';
  readonly otherMethodOfShipment: string;
  readonly frequency: 'ongoing' | 'one-time';
  readonly estimatedVolume: number;
  readonly unitOfMeasure: string;
  readonly dominantColor: string;
  readonly otherColor: string[];
  readonly odorStrength: string;
  readonly odorDescription: string;
  readonly stateApprovalWasteCode: string;
  readonly stateApprovalWasteCodeNotApplicable: boolean | null;

  // attachment step
  readonly additionalSupplements: AdditionalSupplement[];
  readonly stateAttachments: Attachment[];
  readonly internalOnlyAttachments: Attachment[];

  constructor(data?: ExpressWasteProfile) {
    super(data);

    this.type = 'express';
    this.meta = this.createMeta(data?.meta);
    this.wasteTruxCode = this.createTruxDescription(data?.wasteTruxCode);

    // must be false for express, so assume false unless explicitly set
    this.sampleTaken =
      DomainModelHelper.ensureDataIsBoolean(data?.sampleTaken) || false;

    this.methodOfShipment = this.createMethodOfShipment(data?.methodOfShipment);
    this.otherMethodOfShipment =
      DomainModelHelper.ensureDataIsString(data?.otherMethodOfShipment) || '';

    this.frequency = this.createFrequency(data?.frequency);

    this.estimatedVolume = DomainModelHelper.ensureDataIsNumber(
      data?.estimatedVolume
    );
    this.unitOfMeasure = DomainModelHelper.ensureDataIsString(
      data?.unitOfMeasure
    );
    this.dominantColor = DomainModelHelper.ensureDataIsString(
      data?.dominantColor
    );
    this.otherColor = DomainModelHelper.ensureDataIsStringArray(
      data?.otherColor
    );
    this.odorStrength = DomainModelHelper.ensureDataIsString(
      data?.odorStrength
    );
    this.odorDescription = DomainModelHelper.ensureDataIsString(
      data?.odorDescription
    );
    this.stateApprovalWasteCode = DomainModelHelper.ensureDataIsString(
      data?.stateApprovalWasteCode
    );
    this.stateApprovalWasteCodeNotApplicable =
      DomainModelHelper.ensureDataIsBoolean(
        data?.stateApprovalWasteCodeNotApplicable
      );

    this.additionalSupplements = data
      ? data.additionalSupplements
        ? this.createAdditionalSupplements(data.additionalSupplements)
        : ExpressWasteProfileModel.DEFAULT_CONFIG.additionalSupplements
      : ExpressWasteProfileModel.DEFAULT_CONFIG.additionalSupplements;

    this.stateAttachments = data ? data.stateAttachments : null;
    this.internalOnlyAttachments = data ? data.internalOnlyAttachments : null;
  }

  static readonly isExpressWaste = (
    wasteProfile: WasteProfile
  ): wasteProfile is ExpressWasteProfile => wasteProfile.type === 'express';

  static readonly isAsbestos = (wasteProfile: ExpressWasteProfile): boolean =>
    ExpressWasteProfileModel.getAsbestosType(wasteProfile) !== 'NONE';

  static readonly getAsbestosType = (
    wasteProfile: ExpressWasteProfile
  ): 'FRIABLE' | 'NONFRIABLE' | 'NONE' => {
    if (wasteProfile.wasteTruxCode.materialCode === 'VA') {
      return 'FRIABLE';
    } else if (wasteProfile.wasteTruxCode.materialCode === 'VD') {
      return 'NONFRIABLE';
    }

    return 'NONE';
  };

  // TODO: Clean this up when refactoring to use zod schemas
  // TODO: we can eliminate the parseWasteProfile logic and just do something like this right on the model
  public getValue(): ExpressWasteProfile {
    return {
      id: this.id,
      schemaVersion: this.schemaVersion,
      type: this.type,
      active: this.active,
      version: this.version,
      meta: this.meta,
      createdBy: this.createdBy,
      createdDate: this.createdDate,
      lastModifiedBy: this.lastModifiedBy,
      lastModifiedDate: this.lastModifiedDate,
      creator: this.creator,
      customerName: this.customerName,
      status: this.status,
      internalStatus: this.internalStatus,
      isLegacyProfile: this.isLegacyProfile,
      isDuplicate: this.isDuplicate,
      duplicatedFromId: this.duplicatedFromId,
      landfill: this.landfill,
      wasteType: this.wasteType,
      frequency: this.frequency,
      processGeneratingWaste: this.processGeneratingWaste,
      generator: this.generator,
      destination: this.destination,
      checkList: this.checkList,
      name: this.name,
      number: this.number,
      offlineApproved: this.offlineApproved,
      offlineExists: this.offlineExists,
      signNowDocUrl: this.signNowDocUrl,
      signNowDocument: this.signNowDocument,
      signatures: this.signatures,
      signedDocument: this.signedDocument,
      docusignData: this.docusignData,
      certificationComments: this.certificationComments,
      certificationName: this.certificationName,
      certificationTitle: this.certificationTitle,
      certificationEmail: this.certificationEmail,
      certificationSignature: this.certificationSignature,
      yearCode: this.yearCode,
      logbookSequenceNumber: this.logbookSequenceNumber,
      logbookId: this.logbookId,
      logbookNotes: this.logbookNotes,
      truxMaterialDescription: this.truxMaterialDescription,
      approvalNotes: this.approvalNotes,
      analyticalRequiredOnNextRecert: this.analyticalRequiredOnNextRecert,
      incompleteNotes: this.incompleteNotes,
      rejectionNotes: this.rejectionNotes,
      salesRepName: this.salesRepName,
      salesRepNumber: this.salesRepNumber,
      coordinator: this.coordinator,
      recertAnalyticalCustomerNote: this.recertAnalyticalCustomerNote,
      approverName: this.approverName,
      incompleteBy: this.incompleteBy,
      rejectedBy: this.rejectedBy,
      submittedDate: this.submittedDate,
      certifiedDate: this.certifiedDate,
      expectedResponseDate: this.expectedResponseDate,
      approvedDate: this.approvedDate,
      rejectedDate: this.rejectedDate,
      incompleteDate: this.incompleteDate,
      expiryDate: this.expiryDate,
      isRecertification: this.isRecertification,
      isChange: this.isChange,
      isExpired: this.isExpired,
      pendingRecertification: this.pendingRecertification,
      pendingChangeRequest: this.pendingChangeRequest,
      log: this.log,
      commentLog: this.commentLog,
      thirdPartyAuth: this.thirdPartyAuth,
      thirdPartyAuthAttachment: this.thirdPartyAuthAttachment,
      additionalAdminSupplements: this.additionalAdminSupplements,
      internalAdminAttachments: this.internalAdminAttachments,
      thirdPartyAuthAdminAttachment: this.thirdPartyAuthAdminAttachment,
      stateInternalAdminAttachments: this.stateInternalAdminAttachments,
      stateApprovalWasteCodeNotApplicable:
        this.stateApprovalWasteCodeNotApplicable,
      additionalComments: this.additionalComments,
      regulatoryIdNumberNotApplicable: this.regulatoryIdNumberNotApplicable,

      // Express Waste Profile Fields
      wasteTruxCode: this.wasteTruxCode,
      sampleTaken: this.sampleTaken,
      methodOfShipment: this.methodOfShipment,
      otherMethodOfShipment: this.otherMethodOfShipment,
      estimatedVolume: this.estimatedVolume,
      unitOfMeasure: this.unitOfMeasure,
      dominantColor: this.dominantColor,
      otherColor: this.otherColor,
      odorStrength: this.odorStrength,
      odorDescription: this.odorDescription,
      stateApprovalWasteCode: this.stateApprovalWasteCode,
      additionalSupplements: this.additionalSupplements,
      stateAttachments: this.stateAttachments,
      internalOnlyAttachments: this.internalOnlyAttachments,
    };
  }

  protected createMeta(
    data?: ExpressWasteProfileMeta
  ): ExpressWasteProfileMetaModel {
    if (data) {
      return new ExpressWasteProfileMetaModel(data);
    }

    return new ExpressWasteProfileMetaModel();
  }

  private createTruxDescription(
    data?: TruxDescription
  ): TruxDescriptionModel | null {
    if (data) {
      return new TruxDescriptionModel(data);
    }

    return null;
  }

  private createMethodOfShipment(
    data?: string
  ): ExpressWasteProfile['methodOfShipment'] {
    switch (data?.toLowerCase()) {
      case 'bulk':
        return 'bulk';
      case 'drum':
        return 'drum';
      case 'bagged':
        return 'bagged';
      case 'other':
        return 'other';
      default:
        return null;
    }
  }

  private createFrequency(data?: string): ExpressWasteProfile['frequency'] {
    switch (data?.toLowerCase()) {
      case 'ongoing':
        return 'ongoing';
      case 'one-time':
        return 'one-time';
      default:
        return null;
    }
  }

  private createAdditionalSupplements(
    additionalSupplements: AdditionalSupplement[]
  ): AdditionalSupplement[] {
    if (!Array.isArray(additionalSupplements)) {
      return [];
    }

    return additionalSupplements
      .filter((additionalSupplement) => !!additionalSupplement)
      .map(
        (additionalSupplement) =>
          new AdditionalSupplementModel(additionalSupplement)
      );
  }
}
